import { rebaseShapes, snapshotValueEqual, type Shape } from '@vd/annotation-core';

const PENDING_VERSION = 3 as const;

export interface PendingAnnotationSnapshot {
  version: typeof PENDING_VERSION;
  ownerUserId: string;
  writerId: string;
  revision: number;
  revisionToken: string;
  base: Shape[];
  current: Shape[];
}

export interface PendingAnnotationRecord extends PendingAnnotationSnapshot {
  storageKey: string;
}

export type PendingAnnotationWriteResult = 'pending' | 'cleared' | 'foreign' | 'failed';

export interface AnnotationPendingStorage {
  readonly length?: number;
  key?(index: number): string | null;
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

export function pendingAnnotationKey(
  ticketId: string,
  ownerUserId: string,
  writerId: string,
  revision: number,
  revisionToken: string,
): string {
  return `vd-annot-pending-v3:${encodeURIComponent(ticketId)}:${encodeURIComponent(ownerUserId)}:${encodeURIComponent(writerId)}:${revision}:${encodeURIComponent(revisionToken)}`;
}

function pendingAnnotationPrefix(ticketId: string, ownerUserId: string): string {
  return `vd-annot-pending-v3:${encodeURIComponent(ticketId)}:${encodeURIComponent(ownerUserId)}:`;
}

function isShapeArray(value: unknown): value is Shape[] {
  return Array.isArray(value) && value.every((shape) => {
    if (!shape || typeof shape !== 'object') return false;
    const record = shape as Record<string, unknown>;
    return typeof record.id === 'string' && typeof record.kind === 'string';
  });
}

export function parsePendingAnnotations(raw: string | null): PendingAnnotationSnapshot | null {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as Partial<PendingAnnotationSnapshot>;
    if (parsed.version !== PENDING_VERSION
      || typeof parsed.ownerUserId !== 'string'
      || parsed.ownerUserId.length === 0
      || typeof parsed.writerId !== 'string'
      || parsed.writerId.length === 0
      || !Number.isSafeInteger(parsed.revision)
      || parsed.revision! < 1
      || typeof parsed.revisionToken !== 'string'
      || parsed.revisionToken.length === 0
      || !isShapeArray(parsed.base)
      || !isShapeArray(parsed.current)) return null;
    return {
      version: PENDING_VERSION,
      ownerUserId: parsed.ownerUserId,
      writerId: parsed.writerId,
      revision: parsed.revision!,
      revisionToken: parsed.revisionToken,
      base: parsed.base,
      current: parsed.current,
    };
  } catch {
    return null;
  }
}

export function readPendingAnnotations(
  storage: AnnotationPendingStorage,
  ticketId: string,
  ownerUserId: string,
): PendingAnnotationRecord[] {
  const recordsByWriter = new Map<string, PendingAnnotationRecord>();
  try {
    const prefix = pendingAnnotationPrefix(ticketId, ownerUserId);
    const keys: string[] = [];
    const length = storage.length ?? 0;
    for (let index = 0; index < length; index += 1) {
      const key = storage.key?.(index);
      if (key?.startsWith(prefix)) keys.push(key);
    }
    keys.sort();
    for (const key of keys) {
      const raw = storage.getItem(key);
      const pending = parsePendingAnnotations(raw);
      if (!pending || pending.ownerUserId !== ownerUserId) continue;
      if (pendingAnnotationKey(
        ticketId,
        ownerUserId,
        pending.writerId,
        pending.revision,
        pending.revisionToken,
      ) !== key) continue;
      const previous = recordsByWriter.get(pending.writerId);
      if (!previous || pending.revision > previous.revision) {
        recordsByWriter.set(pending.writerId, { ...pending, storageKey: key });
      }
    }
    return [...recordsByWriter.values()].sort((a, b) => a.writerId.localeCompare(b.writerId));
  } catch {
    return [...recordsByWriter.values()].sort((a, b) => a.writerId.localeCompare(b.writerId));
  }
}

function readWriterRecords(
  storage: AnnotationPendingStorage,
  ticketId: string,
  ownerUserId: string,
  writerId: string,
): PendingAnnotationRecord[] {
  const writerPrefix = `${pendingAnnotationPrefix(ticketId, ownerUserId)}${encodeURIComponent(writerId)}:`;
  const records: PendingAnnotationRecord[] = [];
  const length = storage.length ?? 0;
  for (let index = 0; index < length; index += 1) {
    const key = storage.key?.(index);
    if (!key?.startsWith(writerPrefix)) continue;
    const pending = parsePendingAnnotations(storage.getItem(key));
    if (!pending
      || pending.ownerUserId !== ownerUserId
      || pending.writerId !== writerId
      || pendingAnnotationKey(
        ticketId,
        ownerUserId,
        writerId,
        pending.revision,
        pending.revisionToken,
      ) !== key) continue;
    records.push({ ...pending, storageKey: key });
  }
  return records;
}

/**
 * Store the server base and the exact local working copy as one atomic JSON
 * value. The base is required to distinguish local moves and deletions from
 * unrelated remote changes after a reload.
 */
export function persistPendingAnnotations(
  storage: AnnotationPendingStorage,
  ticketId: string,
  ownerUserId: string,
  writerId: string,
  base: Shape[],
  current: Shape[],
): PendingAnnotationWriteResult {
  try {
    const existing = readWriterRecords(storage, ticketId, ownerUserId, writerId);
    if (snapshotValueEqual(base, current)) {
      for (const record of existing) storage.removeItem(record.storageKey);
      return 'cleared';
    }
    const revision = existing.reduce((highest, record) => Math.max(highest, record.revision), 0) + 1;
    const revisionToken = globalThis.crypto.randomUUID();
    const key = pendingAnnotationKey(ticketId, ownerUserId, writerId, revision, revisionToken);
    storage.setItem(key, JSON.stringify({
      version: PENDING_VERSION,
      ownerUserId,
      writerId,
      revision,
      revisionToken,
      base,
      current,
    } satisfies PendingAnnotationSnapshot));
    // The new immutable revision is durable before superseded revisions are
    // retired, so interruption can leave duplication but cannot lose work.
    for (const record of existing) storage.removeItem(record.storageKey);
    return 'pending';
  } catch {
    // localStorage is a recovery buffer, not the primary save path. Quota,
    // privacy-mode, and storage-policy failures must not abort the server save.
    return 'failed';
  }
}

/** Apply pending local additions, modifications, and deletions over fresh server truth. */
export function recoverPendingAnnotations(
  server: Shape[],
  pending: PendingAnnotationSnapshot[],
): Shape[] {
  return pending.reduce(
    (merged, snapshot) => rebaseShapes(merged, snapshot.current, snapshot.base),
    server,
  );
}

/**
 * Retire recovery inputs incorporated into a confirmed save. Each removal is
 * safe because each revision has an immutable key. A tab that edits after
 * hydration writes a different key, which this cleanup never targets.
 */
export function retireIncorporatedPendingAnnotations(
  storage: AnnotationPendingStorage,
  records: PendingAnnotationRecord[],
): 'retired' | 'failed' {
  try {
    for (const record of records) {
      storage.removeItem(record.storageKey);
    }
    return 'retired';
  } catch {
    return 'failed';
  }
}

/** True when a merged live-event result still contains local work to publish. */
export function shouldRepublishAnnotations(remote: Shape[], merged: Shape[]): boolean {
  return !snapshotValueEqual(remote, merged);
}

/**
 * Advance one ticket's durable pending record after a confirmed save. A late
 * response may arrive after a newer local edit or after the canvas switches
 * tickets, so currentOverride is used only while the same ticket is mounted.
 */
export function acknowledgePendingAnnotations(
  storage: AnnotationPendingStorage,
  ticketId: string,
  ownerUserId: string,
  writerId: string,
  sent: Shape[],
  confirmed: Shape[],
  currentOverride?: Shape[],
): PendingAnnotationWriteResult {
  const pending = readPendingAnnotations(storage, ticketId, ownerUserId)
    .find((record) => record.writerId === writerId);
  const current = currentOverride ?? pending?.current;
  if (!current) return 'cleared';
  const base = pending?.base ?? sent;
  const rebasedCurrent = snapshotValueEqual(current, sent)
    ? current
    : rebaseShapes(confirmed, current, base);
  return persistPendingAnnotations(
    storage,
    ticketId,
    ownerUserId,
    writerId,
    confirmed,
    rebasedCurrent,
  );
}
